import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
    Box,
    Typography,
} from "@mui/material";
import { Input, Select, H4 } from '@tviklax/ui-components';
import { useForm, Controller } from "react-hook-form";
import VerifiedIcon from "@mui/icons-material/Verified";
import {
    SELLER_COMPANY_INFORMATION_VALIDATE,
    SELLER_KYC_DOCUMENT_UPLOAD,
    BASE_URL,
    VERIFICATION_API_BASE_URL,
    VERIFY_GSTIN_NUMBER,
    VERIFY_IEC_CODE,
} from "../../../../config/api";
import { toast } from "react-toastify";
import FullPageLoader from "../../../../components/common/FullPageLoader";
import {
    Autocomplete, useLoadScript
} from "@react-google-maps/api";
import { SellerRegistrationFormData } from "../../components";

interface Option {
    id: string | number;
    name: string;
    value?: string;
}

interface CompanyInformationFormProps {
    formData: SellerRegistrationFormData;
    setFormData: React.Dispatch<React.SetStateAction<SellerRegistrationFormData>>;
    handleChange: (e: any) => void;
    handleNext: () => void;
    businesstypeLoading: boolean;
    businessTypesOptions: Option[];
    country: string;

    stateOptions: Option[];
    formId: string;
    setNextDisabled?: (disabled: boolean) => void;
    setNextLabel?: (label?: string) => void;
}

const libraries: ("places")[] = ["places"];

const CompanyInformationForm: React.FC<CompanyInformationFormProps> = ({
    formData,
    setFormData,
    handleChange,
    handleNext,
    businesstypeLoading,
    businessTypesOptions,
    country,

    stateOptions,
    formId,
    setNextDisabled,
    setNextLabel,
}) => {
    const [fullPageLoading, setFullPageLoading] = useState(false);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [showVerifyGstinBtn, setShowVerifyGstinBtn] = useState(false);
    const [verifyGstinBtnDisabled, setVerifyGstinBtnDisabled] = useState(false);
    const [gstinVerified, setGstinVerified] = useState(
        formData?.gstinVerified || false
    );

    const [gstinVerifiedValue, setGstinVerifiedValue] = useState(
        formData?.gstinVerified ? formData?.gstin : ""
    );

    const [showVerifyIECBtn, setShowVerifyIECBtn] = useState(false);
    const [verifyIECBtnDisabled, setVerifyIECBtnDisabled] = useState(false);
    const [iecVerified, setIecVerified] = useState(
        formData?.iecVerified || false
    );
    const [supplierKycUploading, setSupplierKycUploading] = useState(false);
    const [supplierKycUploadError, setSupplierKycUploadError] = useState<string | null>(null);
    const [supplierKycFileName, setSupplierKycFileName] = useState(
        formData?.supplierKycDocumentName || ""
    );

    const [searchResult, setSearchResult] = useState<google.maps.places.Autocomplete | null>(null);
    const [mapError, setMapError] = useState(false);
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
        libraries,
    });

    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const geocoderRef = useRef<google.maps.Geocoder | null>(null);



    useEffect(() => {
        if (isLoaded && !geocoderRef.current) {
            geocoderRef.current = new window.google.maps.Geocoder();
        }
    }, [isLoaded]);

    function onLoad(autocomplete: google.maps.places.Autocomplete) {
        try {
            autocomplete.setComponentRestrictions({ country: country });
            autocomplete.setFields(["formatted_address", "geometry", "address_components", "name"]);
            setSearchResult(autocomplete);
            autocompleteRef.current = autocomplete;
        } catch (err) {
            setMapError(true);
            console.error("Google Autocomplete failed to load:", err);
        }
    }



    function onPlaceChanged() {
        if (searchResult) {
            const place = searchResult.getPlace();

            if (autocompleteRef.current !== null) {
                if (place && place.geometry && place.geometry.location) {
                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();
                    setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
                }
            }

            var formatted_address = place.formatted_address || "";
            var street_number = "";
            var route = "";
            var sublocality_level_2 = "";
            var sublocality_level_1 = "";
            var neighborhood = "";
            var locality = "";
            var state_code = "";
            var postal_code = "";
            var country_code = "";

            if (place.address_components) {
                for (var i = 0; i < place.address_components.length; i++) {
                    for (var j = 0; j < place.address_components[i].types.length; j++) {
                        if (place.address_components[i].types[j] == "street_number") {
                            street_number = place.address_components[i].long_name;
                        }
                        if (place.address_components[i].types[j] == "route") {
                            route = place.address_components[i].long_name;
                        }
                        if (place.address_components[i].types[j] == "sublocality_level_2") {
                            sublocality_level_2 = place.address_components[i].long_name;
                        }
                        if (place.address_components[i].types[j] == "sublocality_level_1") {
                            sublocality_level_1 = place.address_components[i].long_name;
                        }
                        if (place.address_components[i].types[j] == "neighborhood") {
                            neighborhood = place.address_components[i].long_name;
                        }
                        if (place.address_components[i].types[j] == "locality") {
                            locality = place.address_components[i].long_name;
                        }
                        if (
                            place.address_components[i].types[j] ==
                            "administrative_area_level_1"
                        ) {
                            state_code = place.address_components[i].short_name;
                        }
                        if (place.address_components[i].types[j] == "postal_code") {
                            postal_code = place.address_components[i].long_name;
                        }
                        if (place.address_components[i].types[j] == "country") {
                            country_code = place.address_components[i].short_name;
                        }
                    }
                }
            }

            var street_address = street_number + " " + route;
            if (neighborhood != "") {
                street_address += " " + neighborhood;
            }
            if (sublocality_level_2 != "") {
                street_address += " " + sublocality_level_2;
            }
            if (sublocality_level_1 != "") {
                street_address += " " + sublocality_level_1;
            }
            street_address = street_address.trim();

            const check_state_code_exists = stateOptions.some(
                (state) => state.value === state_code
            );
            if (!check_state_code_exists) state_code = "";

            const companyAddressData: Partial<SellerRegistrationFormData> = {
                companyaddress: formatted_address,
                address_line2: street_address,
                city: locality,
                state: state_code,
                pincode: postal_code,
                country: country_code,
            };
            setFormData((prev) => ({ ...prev, ...companyAddressData }));
            Object.entries(companyAddressData).forEach(([field, value]) => {
                setValue(field as any, value);
                clearErrors(field as any);
            });
        } else {
            alert("Please enter a valid location.");
        }
    }

    const {
        control,
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        clearErrors,
        setError,
    } = useForm({
        mode: "all",
        defaultValues: formData,
    });

    const validateGstin = (e: any) => {
        const gstinRegex =
            /^[0-9]{2}[A-Z]{3}[ABCFGHLJPTF]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (gstinRegex.test(e.target.value.trim())) {
            setShowVerifyGstinBtn(true);
            checkIfGstinVerified(e.target.value);
        } else {
            setShowVerifyGstinBtn(false);
            setGstinVerified(false);
            setFormData((prev) => ({ ...prev, gstinVerified: false }));
        }
    };

    const checkIfGstinVerified = (gstinVal: string) => {
        if (gstinVerifiedValue === gstinVal) {
            setGstinVerified(true);
            setShowVerifyGstinBtn(false);
            setFormData((prev) => ({ ...prev, gstinVerified: true }));
        } else {
            setGstinVerified(false);
            setShowVerifyGstinBtn(true);
            setFormData((prev) => ({ ...prev, gstinVerified: false }));
        }
    };

    const verifyGstin = () => {
        setVerifyGstinBtnDisabled(true);
        axios
            .post(VERIFICATION_API_BASE_URL + VERIFY_GSTIN_NUMBER, {
                gstin: formData?.gstin,
            })
            .then((response) => {
                if (response?.data?.success && response?.data?.code === 200) {
                    setGstinVerified(true);
                    setGstinVerifiedValue(formData?.gstin);
                    setShowVerifyGstinBtn(false);
                    const companyData = response?.data?.data;
                    setFormData((prev) => ({ ...prev, ...companyData }));
                    setFormData((prev) => ({ ...prev, gstinVerified: true }));

                    const fullAddress = [
                        companyData.companyaddress,
                        companyData.city,
                        companyData.state,
                        companyData.pincode,
                        companyData.country,
                    ].filter(Boolean).join(", ");

                    if (geocoderRef.current) {
                        geocoderRef.current.geocode({ address: fullAddress }, (results, status) => {
                            if (status === "OK" && results && results[0]) {
                                const lat = results[0].geometry.location.lat();
                                const lng = results[0].geometry.location.lng();
                                setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
                            } else {
                                console.warn("Geocoding failed for GSTIN address:", status);
                            }
                        });
                    }

                    clearErrors("gstin");
                    Object.entries(companyData).forEach(([field, value]) => {
                        setValue(field as any, value);
                        clearErrors(field as any);
                    });
                } else {
                    setError("gstin", {
                        type: "manual",
                        message: response?.data?.message,
                    });
                }
            })
            .catch((error) => {
                if (
                    !error?.response?.data?.success &&
                    error?.response?.status === 422
                ) {
                    const validation_errors = error?.response?.data?.message;
                    Object.entries(validation_errors).forEach(([field, messages]: [string, any]) => {
                        setError(field as any, {
                            type: "manual",
                            message: messages[0],
                        });
                    });
                } else {
                    toast.error(error?.response?.data?.message);
                }
            })
            .finally(() => {
                setVerifyGstinBtnDisabled(false);
            });
    };

    const businessTypeChanged = () => {
        clearErrors("ssn_number" as any);
        clearErrors("ein_number" as any);
    };

    const validateIEC = (e: any) => {
        const iecRegex = /^[0-9A-Z]{10}$/;
        if (iecRegex.test(e.target.value.trim())) {
            setShowVerifyIECBtn(true);
        } else {
            setShowVerifyIECBtn(false);
        }
    };

    const verifyIEC = () => {
        setVerifyIECBtnDisabled(true);
        axios
            .post(VERIFICATION_API_BASE_URL + VERIFY_IEC_CODE, {
                iec_code: formData?.iec_code,
            })
            .then((response) => {
                if (response?.data?.success && response?.data?.code === 200) {
                    setIecVerified(true);
                    setShowVerifyIECBtn(false);
                    const iecResponse = response?.data?.data?.iecResponse;
                    setFormData((prev) => ({ ...prev, ...iecResponse }));
                    setFormData((prev) => ({ ...prev, iecVerified: true }));
                } else {
                    setError("iec_code", {
                        type: "manual",
                        message: response?.data?.message,
                    });
                }
            })
            .catch((error) => {
                if (
                    !error?.response?.data?.success &&
                    error?.response?.status === 422
                ) {
                    const validation_errors = error?.response?.data?.message;
                    Object.entries(validation_errors).forEach(([field, messages]: [string, any]) => {
                        setError(field as any, {
                            type: "manual",
                            message: messages[0],
                        });
                    });
                } else {
                    toast.error(error?.response?.data?.message);
                }
            })
            .finally(() => {
                setVerifyIECBtnDisabled(false);
            });
    };

    const uploadSupplierKycDocument = async (file: File) => {
        if (!formData?.email) {
            toast.error("Please enter email before uploading Supplier KYC document.");
            return;
        }

        setSupplierKycUploading(true);
        setSupplierKycUploadError(null);

        try {
            const payload = new FormData();
            payload.append("email", formData.email);
            payload.append("file", file);

            const response = await axios.post(`${BASE_URL}${SELLER_KYC_DOCUMENT_UPLOAD}`, payload, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response?.data?.fileUrl) {
                setSupplierKycFileName(file.name);
                setFormData((prev) => ({
                    ...prev,
                    supplierKycDocumentUrl: response.data.fileUrl,
                    supplierKycDocumentName: file.name,
                }));
                setValue("supplierKycDocumentUrl" as any, response.data.fileUrl);
                clearErrors("supplierKycDocumentUrl" as any);
                toast.success("Supplier KYC document uploaded and will be reused in forex compliance.");
            } else {
                throw new Error("Supplier KYC upload did not return a file URL");
            }
        } catch (error: any) {
            const message = error?.response?.data?.detail || error?.response?.data?.message || error?.message || "Supplier KYC upload failed";
            setSupplierKycUploadError(message);
            toast.error(message);
        } finally {
            setSupplierKycUploading(false);
        }
    };

    const onSubmit = () => {
        if (btnDisabled) return;
        if (country === "IN") {
            if (!gstinVerified) {
                setShowVerifyGstinBtn(true);
                setError("gstin", {
                    type: "manual",
                    message: "Please verify GSTIN number",
                });
                return false;
            }
        }

        if (!formData?.supplierKycDocumentUrl) {
            setError("supplierKycDocumentUrl" as any, {
                type: "manual",
                message: "Supplier KYC document is required",
            });
            toast.error("Please upload the Supplier KYC document to continue.");
            return false;
        }

        setFullPageLoading(true);
        setBtnDisabled(true);
        setNextDisabled?.(true);
        setNextLabel?.("Validating...");
        axios
            .post(SELLER_COMPANY_INFORMATION_VALIDATE, formData)
            .then((response) => {
                if (response?.data?.success && response?.data?.code === 200) {
                    handleNext();
                } else {
                    toast.error(response?.data?.message);
                }
            })
            .catch((error) => {
                if (
                    !error?.response?.data?.success &&
                    error?.response?.status === 422
                ) {
                    const validation_errors = error?.response?.data?.message;
                    Object.entries(validation_errors).forEach(([field, messages]: [string, any]) => {
                        setError(field as any, {
                            type: "manual",
                            message: messages[0],
                        });
                    });
                } else {
                    toast.error(error?.response?.data?.message);
                }
            })
            .finally(() => {
                setBtnDisabled(false);
                setNextDisabled?.(false);
                setNextLabel?.(undefined);
                setFullPageLoading(false);
            });
    };

    return (
        <form id={formId} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
            <FullPageLoader loading={fullPageLoading} color="#f86400" />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ pb: 1, pt: 1 }}>
                    <H4 style={{ fontWeight: 600 }}>Company Information</H4>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 5 }}>
                    <Box>
                        <Box>
                            <Controller
                                name="businesstype"
                                control={control}
                                defaultValue={formData.businesstype}
                                rules={{ required: "Business type is required" }}
                                render={({ field: { onChange, value, ref } }) => (
                                    <Select
                                        ref={ref}
                                        label="Business Type"
                                        name="businesstype"
                                        value={value || ""}
                                        error={errors.businesstype?.message as string}
                                        items={!businesstypeLoading ? (
                                            businessTypesOptions.length > 0 ? (
                                                businessTypesOptions.map((option) => ({
                                                    label: option.name,
                                                    value: option.id.toString(),
                                                }))
                                            ) : []
                                        ) : []}
                                        placeholder={businesstypeLoading ? "Loading..." : "Select Business Type"}
                                        onChange={(e) => {
                                            if (country === "US") {
                                                businessTypeChanged();
                                            }
                                            handleChange(e);
                                            onChange(e);
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    </Box>

                    {country === "US" && (
                        <Box
                            style={{
                                display:
                                    formData.businesstype != "" && formData.businesstype != "13"
                                        ? "block"
                                        : "none",
                            }}
                        >
                            <Input
                                label="Enter EIN Number"
                                {...register("ein_number", {
                                    required:
                                        formData.businesstype != "13"
                                            ? "EIN number is required"
                                            : false,
                                    pattern: {
                                        value: /^\d{2}-\d{7}$/,
                                        message: "Invalid EIN format (Expected: XX-XXXXXXX)",
                                    },
                                })}
                                onChange={(e) => {
                                    handleChange(e);
                                }}
                                error={errors.ein_number?.message as string}
                                value={formData.ein_number}
                            />
                        </Box>
                    )}
                    {country === "US" && (
                        <Box
                            style={{
                                display:
                                    formData.businesstype != "" && formData.businesstype != "14"
                                        ? "block"
                                        : "none",
                            }}
                        >
                            <Input
                                label="Enter SSN Number"
                                {...register("ssn_number", {
                                    required:
                                        formData.businesstype == "13"
                                            ? "SSN number is required"
                                            : false,
                                    pattern: {
                                        value: /^\d{3}-\d{2}-\d{4}$/,
                                        message: "Invalid SSN format (Expected: XXX-XX-XXXX)",
                                    },
                                })}
                                onChange={(e) => {
                                    handleChange(e);
                                }}
                                error={errors.ssn_number?.message as string}
                                value={formData.ssn_number}
                            />
                        </Box>
                    )}

                    {country === "IN" && (
                        <Box>
                            <Box>
                                <Input
                                    label="Enter GSTIN"
                                    autoComplete="off"
                                    {...register("gstin", {
                                        required: "GSTIN is required",
                                        pattern: {
                                            value:
                                                /^[0-9]{2}[A-Z]{3}[ABCFGHLJPTF]{1}[A-Z]{1}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                                            message: "Invalid GSTIN format",
                                        },
                                    })}
                                    onChange={(e) => {
                                        handleChange(e);
                                        validateGstin(e);
                                    }}
                                    disabled={verifyGstinBtnDisabled}
                                    error={errors.gstin?.message as string}
                                    value={formData.gstin}
                                    style={{ marginBottom: '1rem' }}
                                    endAdornment={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {showVerifyGstinBtn && !gstinVerified && (
                                                <Box
                                                    onClick={!verifyGstinBtnDisabled ? verifyGstin : undefined}
                                                    sx={{
                                                        fontSize: "14px",
                                                        fontWeight: "500",
                                                        padding: "4px 8px",
                                                        backgroundColor: verifyGstinBtnDisabled ? "#ccc" : "#f86400",
                                                        color: "#fff",
                                                        borderRadius: "4px",
                                                        cursor: verifyGstinBtnDisabled ? "not-allowed" : "pointer",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    {verifyGstinBtnDisabled ? "Verifying..." : "Verify"}
                                                </Box>
                                            )}
                                            {gstinVerified && <VerifiedIcon sx={{ color: "#22C55E" }} />}
                                        </Box>
                                    }
                                />
                            </Box>
                        </Box>
                    )}

                    {country === "IN" && (
                        <Box>
                            <Box>
                                <Input
                                    label="Enter IEC Code"
                                    autoComplete="off"
                                    {...register("iec_code", {
                                        pattern: {
                                            value: /^[0-9A-Z]{10}$/,
                                            message: "Invalid ICE code format",
                                        },
                                    })}
                                    onChange={(e) => {
                                        handleChange(e);
                                        validateIEC(e);
                                    }}
                                    disabled={iecVerified || verifyIECBtnDisabled}
                                    error={errors.iec_code?.message as string}
                                    value={formData.iec_code}
                                    style={{ marginBottom: '1rem' }}
                                    endAdornment={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {showVerifyIECBtn && !iecVerified && (
                                                <Box
                                                    onClick={!verifyIECBtnDisabled ? verifyIEC : undefined}
                                                    sx={{
                                                        fontSize: "14px",
                                                        fontWeight: "500",
                                                        padding: "4px 8px",
                                                        backgroundColor: verifyIECBtnDisabled ? "#ccc" : "#f86400",
                                                        color: "#fff",
                                                        borderRadius: "4px",
                                                        cursor: verifyIECBtnDisabled ? "not-allowed" : "pointer",
                                                        whiteSpace: "nowrap"
                                                    }}
                                                >
                                                    {verifyIECBtnDisabled ? "Verifying..." : "Verify"}
                                                </Box>
                                            )}
                                            {iecVerified && <VerifiedIcon sx={{ color: "#22C55E" }} />}
                                        </Box>
                                    }
                                />
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
                            Supplier KYC Document
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: '#6b7280', mb: 1 }}>
                            Upload one supplier KYC document now. It will be reused automatically in forex compliance.
                        </Typography>
                        <Box
                            sx={{
                                border: '1px dashed #f86400',
                                borderRadius: 2,
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                backgroundColor: '#fffaf5',
                            }}
                        >
                            <Box
                                component="label"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 'fit-content',
                                    px: 2,
                                    py: 1,
                                    borderRadius: 1.5,
                                    backgroundColor: supplierKycUploading ? '#d1d5db' : '#f86400',
                                    color: '#fff',
                                    cursor: supplierKycUploading ? 'not-allowed' : 'pointer',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                }}
                            >
                                {supplierKycUploading ? 'Uploading...' : (supplierKycFileName ? 'Replace KYC Document' : 'Upload KYC Document')}
                                <input
                                    type="file"
                                    hidden
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    disabled={supplierKycUploading}
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            uploadSupplierKycDocument(file);
                                        }
                                        e.currentTarget.value = '';
                                    }}
                                />
                            </Box>
                            <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                Allowed formats: PDF, JPG, JPEG, PNG
                            </Typography>
                            {supplierKycFileName && (
                                <Typography sx={{ fontSize: '13px', color: '#111827', fontWeight: 500 }}>
                                    Uploaded: {supplierKycFileName}
                                </Typography>
                            )}
                            {supplierKycUploadError && (
                                <Typography sx={{ fontSize: '12px', color: '#dc2626' }}>
                                    {supplierKycUploadError}
                                </Typography>
                            )}
                            {errors.supplierKycDocumentUrl && (
                                <Typography sx={{ fontSize: '12px', color: '#dc2626' }}>
                                    {errors.supplierKycDocumentUrl.message as string}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box>
                        <Input
                            label="Company Name"
                            {...register("companyname", {
                                required: "Company name is required",
                                minLength: {
                                    value: 3,
                                    message: "Company name must be at least 3 characters",
                                },
                            })}
                            value={formData.companyname}
                            onChange={handleChange}
                            error={errors.companyname?.message as string}
                            style={{ marginBottom: '1rem' }}
                        />
                    </Box>

                    <Box>
                        {!isLoaded ? (
                            <Box>Loading Map...</Box>
                        ) : (
                            <Autocomplete
                                onLoad={onLoad}
                                onPlaceChanged={onPlaceChanged}
                                options={{
                                    types: ["geocode", "establishment"],
                                }}
                            >
                                <Input
                                    label="Company Address"
                                    {...register("companyaddress", {
                                        required: "Company address is required",
                                        minLength: {
                                            value: 10,
                                            message: "Company address must be at least 10 characters",
                                        },
                                    })}
                                    value={formData.companyaddress}
                                    onChange={handleChange}
                                    error={errors.companyaddress?.message as string}
                                    style={{ marginBottom: '1rem' }}
                                />
                            </Autocomplete>
                        )}
                        {(mapError || loadError) && (
                            <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>Error loading map.</Box>
                        )}
                    </Box>
                </Box>

            </Box>
        </form>
    );
};

export default CompanyInformationForm;
